import type { LogConfig, LogMeta } from '#types/logger'
import type { FetchOptions, FetchResponse } from 'ofetch'
import { parseURL } from 'ufo'

export interface ApiLogParams {
  apiName?: string
  path: ComputedRef<string>
  method: string
  options?: FetchOptions
  params: Record<string, string>
  error?: Error
  response?: FetchResponse<any>
  logKey: string
  logConfig: LogConfig
}

const getLogMeta = ({ path, method, options, params, response, error, logKey, apiName }: ApiLogParams): LogMeta => ({
  channel: 'api',
  stackTrace: error?.stack,
  api: {
    apiName,
    path: path.value,
    host: parseURL(response?.url).host,
    method: unref(method || 'get'),
    queryParams: unref(params),
    queryString: options?.query,
    logKey,
    statusCode: response?.status,
    baseUrl: options?.baseURL,
    referrer: options?.headers?.['referer' as const],
    transactionId: options?.headers?.['x-transaction-id']
  }
})

export const addRequest = (
  req: {
    path: string
    method: string
    headers?: HeadersInit
    baseURL?: string
    query?: Record<string, any>
  },
  server
) => {
  const host = server ? 'server' : 'client'
  const query = new URLSearchParams(req.query).toString()
  let baseURL = new Headers(req.headers).get('baseURL') || req.baseURL

  if (req.path.includes('/fapi'))
    baseURL = baseURL?.replace('/api', '')

  useNuxtApp().$logRequest(host, req.method, `${baseURL}${req.path}?${query}`)
}

export const logRequestError = (logParams: ApiLogParams) => {
  try {
    const isPreload = !!logParams.options?.headers?.['Preload' as const]
    let logLevel = 'error'

    if (logParams.error?.name === 'AbortError' || isPreload) { // Generally happens with debounced user input, e.g. search suggestions
      logLevel = 'info'
    }

    log[logLevel](`API Request Error: ${logParams.error}`, getLogMeta(logParams), logParams.logConfig)
  }
  catch (error) {
    log.error(error)
  }
}

export const logResponseError = (logParams: ApiLogParams) => {
  try {
    const errorMessage = logParams.error || logParams.response?.statusText
    log.error(`API Response Error: ${errorMessage}`, getLogMeta(logParams), logParams.logConfig)
  }
  catch (error) {
    log.error(error)
  }
}

export const logResponseSuccess = (logParams: ApiLogParams, logSuccess = true) => {
  if (logParams?.response?.ok) {
    try {
      if (logSuccess) {
        const meta = getLogMeta(logParams)

        log.info('API Success', meta, logParams.logConfig)
      }
    }
    catch (error) {
      log.error(error)
    }
  }
}
